/**
 * Background Music Button component - controls background music playback
 */

import React, { useState, useEffect, useCallback } from 'react';
import { EyeTrackableButton } from './eye-trackable-button';
import { backgroundMusicManager } from '../utils/sound-utils';
import './background-music-button.scss';

interface IBackgroundMusicButtonProps {
  className?: string;
}

export const BackgroundMusicButton: React.FC<IBackgroundMusicButtonProps> = ({
  className = '',
}) => {
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    // Sync with the actual music manager state on mount
    setIsPlaying(backgroundMusicManager.getIsPlaying());

    // Set up an interval to periodically check the state
    // This helps handle cases where the audio state changes externally
    const interval = setInterval(() => {
      const currentState = backgroundMusicManager.getIsPlaying();
      setIsPlaying(currentState);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleToggleMusic = useCallback(() => {
    console.log('BackgroundMusicButton: Toggling music...');
    try {
      const newIsPlaying = backgroundMusicManager.toggle();
      setIsPlaying(newIsPlaying);

      // Force a state update after a short delay to ensure synchronization
      setTimeout(() => {
        const actualState = backgroundMusicManager.getIsPlaying();
        setIsPlaying(actualState);
      }, 100);
    } catch (error) {
      console.error('Error toggling music:', error);
    }
  }, []);

  return (
    <EyeTrackableButton
      className={`background-music-button ${className} ${
        isPlaying ? 'background-music-button--playing' : 'background-music-button--paused'
      }`}
      onClick={handleToggleMusic}
      variant='music'
      data-testid='background-music-button'
      data-gaze-region='true'
      data-gaze-region-margin='1vw'
      ariaLabel={isPlaying ? 'Pause background music' : 'Play background music'}
      allowOffscreenPosition={true}
    >
      <div className='background-music-button__icon'>{isPlaying ? '🔊' : '🔇'}</div>
      <span className='background-music-button__text'>{isPlaying ? 'Music ON' : 'Music OFF'}</span>
    </EyeTrackableButton>
  );
};
